import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, Timestamp, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useLocation } from 'react-router-dom';
import { firestore, functions } from '../../firebase';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { notificationService } from '../../services/notificationService';

interface PendingVerification {
  id: string;
  serviceName?: string;
  displayName?: string;
  email?: string;
  verificationSubmittedAt?: Timestamp;
  verificationSubmissionId?: string;
  createdAt?: Timestamp;
}

const asTimestamp = (value: unknown): Timestamp | undefined =>
  value instanceof Timestamp ? value : undefined;

const formatTimestamp = (value?: Timestamp): string => {
  if (!value) return 'Brak daty';
  const date = value.toDate();
  return `${date.toLocaleDateString('pl-PL')} ${date.toLocaleTimeString('pl-PL')}`;
};

const deriveVerificationSubmissionId = (
  providerData: Record<string, unknown>
): string | undefined => {
  const stored = String(providerData.verificationSubmissionId || '').trim();
  if (/^[A-Za-z0-9_-]{1,256}$/.test(stored)) return stored;
  const submittedAt = providerData.verificationSubmittedAt as
    | { seconds?: number; nanoseconds?: number; _seconds?: number; _nanoseconds?: number }
    | undefined;
  if (!submittedAt) return undefined;
  const seconds = Number(submittedAt.seconds ?? submittedAt._seconds);
  const nanoseconds = Number(submittedAt.nanoseconds ?? submittedAt._nanoseconds ?? 0);
  if (!Number.isSafeInteger(seconds) || !Number.isInteger(nanoseconds)) return undefined;
  if (nanoseconds < 0 || nanoseconds >= 1_000_000_000) return undefined;
  return `legacy-${seconds}-${nanoseconds}`;
};

const AdminVerificationsPage: React.FC = () => {
  const location = useLocation();
  const [pendingVerifications, setPendingVerifications] = useState<PendingVerification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [readbackError, setReadbackError] = useState<string | null>(null);
  const [actionReason, setActionReason] = useState<string>('');
  const handledReadbacksRef = useRef<Set<string>>(new Set());
  const targetCardRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef<boolean>(true);

  const { targetUserId, notificationId } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      targetUserId: String(params.get('userId') || '').trim(),
      notificationId: String(params.get('notificationId') || '').trim(),
    };
  }, [location.search]);

  const approveServiceAdminFn = httpsCallable<
    { userId: string; expectedVerificationSubmissionId: string },
    { success: boolean; result?: 'applied' | 'idempotent'; decisionId?: string }
  >(functions, 'approveServiceAdmin');
  const rejectServiceAdminFn = httpsCallable<
    { userId: string; reason?: string; expectedVerificationSubmissionId: string },
    { success: boolean; result?: 'applied' | 'idempotent'; decisionId?: string }
  >(functions, 'rejectServiceAdmin');

  const fetchPendingVerifications = useCallback(async () => {
    setLoading(true);
    setQueueError(null);
    setActionError(null);
    setReadbackError(null);
    try {
      const pendingProvidersQuery = query(
        collection(firestore, 'serviceProviders'),
        where('verificationStatus', '==', 'pending_approval')
      );
      const providersSnapshot = await getDocs(pendingProvidersQuery);

      const enriched = await Promise.all(
        providersSnapshot.docs.map(async (providerDoc): Promise<PendingVerification> => {
          const providerData = providerDoc.data() as Record<string, unknown>;
          const userSnap = await getDoc(doc(firestore, 'users', providerDoc.id));
          const userData = (userSnap.exists() ? userSnap.data() : {}) as Record<string, unknown>;

          return {
            id: providerDoc.id,
            serviceName: String(providerData.serviceName || '').trim() || undefined,
            displayName: String(userData.displayName || '').trim() || undefined,
            email: String(userData.email || providerData.email || '').trim() || undefined,
            verificationSubmittedAt: asTimestamp(providerData.verificationSubmittedAt),
            verificationSubmissionId: deriveVerificationSubmissionId(providerData),
            createdAt: asTimestamp(userData.createdAt),
          };
        })
      );

      enriched.sort((left, right) => {
        const leftMillis =
          left.verificationSubmittedAt?.toMillis() || left.createdAt?.toMillis() || 0;
        const rightMillis =
          right.verificationSubmittedAt?.toMillis() || right.createdAt?.toMillis() || 0;
        return rightMillis - leftMillis;
      });
      setPendingVerifications(enriched);
    } catch (fetchError) {
      console.error('Error fetching pending verifications:', fetchError);
      setQueueError('Nie udało się pobrać kolejki weryfikacji serwisów. Spróbuj ponownie później.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPendingVerifications();
  }, [fetchPendingVerifications]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resolvedTarget = useMemo(
    () =>
      targetUserId
        ? pendingVerifications.find((verification) => verification.id === targetUserId) || null
        : null,
    [pendingVerifications, targetUserId]
  );

  useEffect(() => {
    if (loading || queueError || !targetUserId || !notificationId || !resolvedTarget) {
      return;
    }

    const readbackKey = `${notificationId}:${targetUserId}`;
    if (handledReadbacksRef.current.has(readbackKey)) {
      return;
    }
    handledReadbacksRef.current.add(readbackKey);

    void notificationService
      .markVerificationNotificationAsRead({
        notificationId,
        providerUserId: targetUserId,
      })
      .then((readBack) => {
        if (readBack) {
          return;
        }
        handledReadbacksRef.current.delete(readbackKey);
        if (mountedRef.current) {
          setReadbackError(
            'Nie udało się potwierdzić tego powiadomienia. Pozostaje ono nieprzeczytane.'
          );
        }
      })
      .catch((readError) => {
        handledReadbacksRef.current.delete(readbackKey);
        console.error('Failed to read back verification notification:', readError);
        if (mountedRef.current) {
          setReadbackError(
            'Nie udało się potwierdzić tego powiadomienia. Pozostaje ono nieprzeczytane.'
          );
        }
      });
  }, [loading, notificationId, queueError, resolvedTarget, targetUserId]);

  useEffect(() => {
    if (loading || queueError || !resolvedTarget || !targetCardRef.current) {
      return;
    }

    targetCardRef.current.focus({ preventScroll: true });
    targetCardRef.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [loading, queueError, resolvedTarget]);

  const targetMissing = Boolean(!loading && !queueError && targetUserId && !resolvedTarget);

  const updateVerificationStatus = async (
    userId: string,
    newStatus: 'approved' | 'changes_requested'
  ) => {
    const verification = pendingVerifications.find((item) => item.id === userId);
    if (!verification?.verificationSubmissionId) {
      setActionError(
        'Nie można podjąć decyzji bez poprawnego identyfikatora cyklu weryfikacji. Odśwież kolejkę.'
      );
      return;
    }
    if (newStatus === 'changes_requested' && !actionReason.trim()) {
      setActionError('Podaj powód poprawek/odrzucenia przed wysłaniem decyzji.');
      return;
    }

    setUpdatingUserId(userId);
    setActionError(null);
    try {
      if (newStatus === 'approved') {
        await approveServiceAdminFn({
          userId,
          expectedVerificationSubmissionId: verification.verificationSubmissionId,
        });
      } else {
        await rejectServiceAdminFn({
          userId,
          reason: actionReason.trim() || undefined,
          expectedVerificationSubmissionId: verification.verificationSubmissionId,
        });
      }
      setPendingVerifications((prevUsers) => prevUsers.filter((user) => user.id !== userId));
      setActionReason('');
    } catch (updateError) {
      console.error(`Error updating verification status for ${userId}:`, updateError);
      setActionError('Nie udało się zapisać decyzji administracyjnej. Spróbuj ponownie.');
    } finally {
      setUpdatingUserId(null);
    }
  };

  return (
    <section className="space-y-4">
      <header className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 sm:p-6">
        <h1 className="text-xl font-semibold text-primary-light-100 sm:text-2xl">
          Weryfikacje serwisów
        </h1>
        <p className="mt-1 text-sm text-primary-light-300">
          Kolejka profili oczekujących na decyzję administratora.
        </p>
      </header>

      {loading && <LoadingSpinner />}

      {queueError && (
        <div className="rounded-xl border border-status-error-500/40 bg-status-error-500/10 p-3 text-sm text-status-error-500">
          <p>{queueError}</p>
          <button
            type="button"
            onClick={() => void fetchPendingVerifications()}
            className="mt-3 inline-flex h-9 items-center justify-center rounded-lg border border-status-error-500/50 px-3 text-xs font-semibold text-primary-light-100"
          >
            Spróbuj ponownie
          </button>
        </div>
      )}

      {actionError && (
        <p className="rounded-xl border border-status-error-500/40 bg-status-error-500/10 p-3 text-sm text-status-error-500">
          {actionError}
        </p>
      )}

      {targetMissing && (
        <p className="rounded-xl border border-status-warning-500/40 bg-status-warning-500/10 p-3 text-sm text-status-warning-100">
          Wskazany serwis nie znajduje się już w kolejce oczekujących weryfikacji. Powiadomienie
          pozostaje nieprzeczytane.
        </p>
      )}

      {readbackError && (
        <p className="rounded-xl border border-status-warning-500/40 bg-status-warning-500/10 p-3 text-sm text-status-warning-100">
          {readbackError}
        </p>
      )}

      {!loading && !queueError && !targetMissing && pendingVerifications.length === 0 && (
        <p className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 text-sm text-primary-light-300">
          Brak oczekujących weryfikacji.
        </p>
      )}

      {!loading && !queueError && pendingVerifications.length > 0 && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-4">
            <label className="mb-2 block text-sm text-primary-light-300">
              Powód poprawek/odrzucenia (wymagany przy odrzuceniu)
            </label>
            <input
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 p-2 text-sm text-primary-light-100 placeholder:text-primary-light-500"
              placeholder="Np. brak numeru telefonu, niekompletny adres"
            />
          </div>

          {pendingVerifications.map((verification) => {
            const isTarget = verification.id === targetUserId;
            return (
              <div
                key={verification.id}
                ref={isTarget ? targetCardRef : undefined}
                tabIndex={isTarget ? -1 : undefined}
                aria-current={isTarget ? 'true' : undefined}
                className={`rounded-2xl border bg-primary-dark-200 p-4 ${
                  isTarget
                    ? 'border-ratuj-300/70 ring-2 ring-ratuj-300/30'
                    : 'border-primary-dark-300/70'
                }`}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-primary-light-100 sm:text-lg">
                      {verification.serviceName || verification.displayName || 'Brak nazwy serwisu'}
                    </h2>
                    <p className="text-sm text-primary-light-400">
                      {verification.email || 'Brak emaila kontaktowego'}
                    </p>
                    <p className="mt-1 text-xs text-primary-light-500">
                      Wysłano do weryfikacji:{' '}
                      {formatTimestamp(verification.verificationSubmittedAt)}
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                    {updatingUserId === verification.id ? (
                      <div className="flex h-10 items-center justify-center sm:w-24">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => updateVerificationStatus(verification.id, 'approved')}
                          className="inline-flex h-10 items-center justify-center rounded-lg bg-status-success-500 px-4 text-sm font-semibold text-white transition hover:bg-status-success-600 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={
                            updatingUserId !== null || !verification.verificationSubmissionId
                          }
                        >
                          Zatwierdź
                        </button>
                        <button
                          onClick={() =>
                            updateVerificationStatus(verification.id, 'changes_requested')
                          }
                          className="inline-flex h-10 items-center justify-center rounded-lg bg-status-error-500 px-4 text-sm font-semibold text-white transition hover:bg-status-error-600 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={
                            updatingUserId !== null || !verification.verificationSubmissionId
                          }
                        >
                          Odeślij do poprawek
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default AdminVerificationsPage;
